import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../services/api';
import { useAtozasTranslate } from '../../context/AtozasLanguageContext';
import { useTheme } from '../../context/ThemeContext';
import LanguageSelector from '../common/LanguageSelector';
import ConfirmationModal from '../common/ConfirmationModal';

const Navbar = ({ onBecomeDriver, onDriverDashboard, onLogout, onHome, onProfile, onRides, user, isDriver = false, isOnDriverDashboard = false, onDriverTabChange, activeDriverTab = 'dashboard', onNotificationClick, unreadCount = 0, onMenuClick }) => {
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useTheme();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const hasNotifications = unreadCount > 0;
  const tLanguage = useAtozasTranslate('Language');
  const tHome = useAtozasTranslate('Home');
  const tDashboard = useAtozasTranslate('Dashboard');
  const tMyRides = useAtozasTranslate('My Rides');
  const tDriverEarnings = useAtozasTranslate('Driver Earnings');
  const tAccount = useAtozasTranslate('Account');
  const tRides = useAtozasTranslate('Rides');
  const tProfile = useAtozasTranslate('Profile');
  const tFeedback = useAtozasTranslate('Feedback');
  const tTerms = useAtozasTranslate('Terms & Conditions');
  const tPrivacy = useAtozasTranslate('Privacy Policy');
  const tLogout = useAtozasTranslate('Logout');
  const tDriverDashboard = useAtozasTranslate('Driver Dashboard');
  const tBecomeDriver = useAtozasTranslate('Become a Driver');

  // Helper function to get profile picture URL
  const getProfileImageUrl = (profilePicture) => {
    if (!profilePicture) return null;
    // If it's a full URL, return as is
    if (profilePicture.startsWith('http')) return profilePicture;
    // Otherwise, construct backend URL using centralized API helper
    const baseUrl = getApiUrl();
    return `${baseUrl}/${profilePicture.replace(/\\/g, '/')}`;
  };

  const handleNotificationClick = () => {
    if (onNotificationClick) {
      onNotificationClick();
    }
    // Don't toggle panel here - let parent component handle navigation
  };
  return (
    <nav className="sticky top-0 z-50 bg-transparent px-3 pt-3 md:bg-transparent md:px-6 md:pt-4 md:shadow-none md:dark:bg-slate-950">
      <div className="mx-auto max-w-7xl px-0 sm:px-0 md:max-w-[1600px] md:px-0 lg:px-0 md:dark:max-w-full">
        <div className="md:overflow-visible md:rounded-[36px]">
        <div className="relative flex min-h-[76px] items-center justify-between rounded-[26px] bg-white/95 px-3 py-3 shadow-[0_18px_36px_-24px_rgba(15,23,42,0.22)] ring-1 ring-black/5 backdrop-blur md:min-h-[86px] md:rounded-[36px] md:border md:border-[#cbded8] md:bg-[#f7fbf9] md:px-6 md:py-3 md:shadow-[0_20px_50px_-34px_rgba(15,23,42,0.42)] md:ring-0 md:dark:rounded-none md:dark:border-transparent md:dark:bg-slate-950 md:dark:shadow-none">
          {/* Menu Icon - Mobile Only */}
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="mr-2 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#f4f7f6] text-slate-700 shadow-[inset_0_0_0_1px_rgba(15,23,42,0.06)] transition-colors hover:text-primary-600 md:hidden"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}

          {/* Logo and App Name */}
          <div className="relative flex items-center space-x-3">
            <button
              onClick={onHome}
              className="flex min-w-0 items-center space-x-3 transition-opacity hover:opacity-90"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#d9e8e3] bg-white p-1 shadow-[0_10px_24px_-18px_rgba(15,23,42,0.28)] dark:border-slate-700 dark:bg-slate-900 md:hidden">
                <img
                  src="/icon-192.png"
                  alt="UttaApp Logo"
                  className="h-full w-full rounded-[14px] object-cover"
                />
              </div>
              {/* Logo - Hidden on mobile */}
              <div className="hidden h-10 w-10 items-center justify-center rounded-full border border-[#d5e7e1] bg-white p-1.5 shadow-[0_12px_30px_-18px_rgba(15,23,42,0.24)] md:flex md:h-14 md:w-14">
                <img
                  src="/icon-192.png"
                  alt="UttaApp Logo"
                  className="h-full w-full rounded-full object-cover"
                />
              </div>
              {/* App Name - Visible on all screens */}
              <h1 className="flex min-w-0 flex-col items-start font-bold leading-tight text-gray-900 dark:text-white md:text-slate-950 md:dark:text-white">
                <span
                  translate="no"
                  className="notranslate block truncate text-[1.35rem] tracking-[-0.04em] text-slate-950 dark:text-white md:text-2xl md:dark:text-white"
                >
                  UttaApp
                </span>
                <span
                  className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500 md:hidden"
                >
                  Ready to ride
                </span>
                <span className="hidden whitespace-nowrap text-xs font-normal opacity-70 md:block md:text-[11px] md:uppercase md:tracking-[0.24em] md:text-slate-500 md:dark:text-slate-400">beta/test version</span>
              </h1>
            </button>
          </div>

          {/* Navigation Items - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-2 rounded-full border border-[#dbeae5] bg-[#edf7f3] p-1.5 shadow-[0_14px_30px_-24px_rgba(15,23,42,0.16)] dark:border-slate-800 dark:bg-slate-900">
            {isOnDriverDashboard ? (
              <>
                {/* Driver Dashboard Tabs */}
                <button
                  onClick={onHome}
                  className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#0f766e] dark:text-slate-300 dark:hover:text-[#5eead4]"
                >
                  {tHome}
                </button>
                <button
                  onClick={() => onDriverTabChange && onDriverTabChange('dashboard')}
                  className={`transition-colors font-medium ${activeDriverTab === 'dashboard'
                      ? 'rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_-20px_rgba(15,118,110,0.7)]'
                      : 'rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#0f766e] dark:text-slate-300 dark:hover:text-[#5eead4]'
                    }`}
                >
                  {tDashboard}
                </button>
                <button
                  onClick={() => onDriverTabChange && onDriverTabChange('rides')}
                  className={`transition-colors font-medium ${activeDriverTab === 'rides'
                      ? 'rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_-20px_rgba(15,118,110,0.7)]'
                      : 'rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#0f766e] dark:text-slate-300 dark:hover:text-[#5eead4]'
                    }`}
                >
                  {tMyRides}
                </button>
                <button
                  onClick={() => onDriverTabChange && onDriverTabChange('earnings')}
                  className={`transition-colors font-medium ${activeDriverTab === 'earnings'
                      ? 'rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_-20px_rgba(15,118,110,0.7)]'
                      : 'rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#0f766e] dark:text-slate-300 dark:hover:text-[#5eead4]'
                    }`}
                >
                  {tDriverEarnings}
                </button>
                <button
                  onClick={() => onDriverTabChange && onDriverTabChange('account')}
                  className={`transition-colors font-medium ${activeDriverTab === 'account'
                      ? 'rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_-20px_rgba(15,118,110,0.7)]'
                      : 'rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#0f766e] dark:text-slate-300 dark:hover:text-[#5eead4]'
                    }`}
                >
                  {tAccount}
                </button>
              </>
            ) : (
              <>
                {/* User Dashboard Tabs */}
                <button
                  onClick={onHome}
                  className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#0f766e] dark:text-slate-300 dark:hover:text-[#5eead4]"
                >
                  {tHome}
                </button>
                <button
                  onClick={onRides || (() => { })}
                  className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#0f766e] dark:text-slate-300 dark:hover:text-[#5eead4]"
                >
                  {tRides}
                </button>
                <button
                  onClick={onProfile}
                  className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#0f766e] dark:text-slate-300 dark:hover:text-[#5eead4]"
                >
                  {tProfile}
                </button>
              </>
            )}
          </div>

          {/* Driver Action Button - Hidden on mobile */}
          <div className="hidden md:flex items-center space-x-3">
            {/* Language Selector */}
            <div className="flex items-center gap-2 rounded-full border border-[#dbeae5] bg-[#edf7f3] p-1.5 shadow-[0_14px_30px_-24px_rgba(15,23,42,0.16)] dark:border-slate-800 dark:bg-slate-900">
              <LanguageSelector
                aria-label={tLanguage}
                title={tLanguage}
                className="max-w-[155px] rounded-full border border-[#d7eae4] bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0f766e] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
              <button
                type="button"
                onClick={() => navigate('/subscription-pricing')}
                className="inline-flex items-center gap-2 rounded-full border border-[#d7eae4] bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:text-[#0f766e] focus:outline-none focus:ring-2 focus:ring-[#0f766e] focus:ring-offset-2 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:text-[#5eead4]"
              >
                <svg className="h-5 w-5 text-[#0f766e] dark:text-[#5eead4]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
                <span className="font-semibold">{tAccount}</span>
              </button>
            </div>

            {isDriver && !isOnDriverDashboard ? (
              <button
                onClick={onDriverDashboard}
                className="landing-primary-button px-6 py-2.5 text-sm"
              >
                {tDriverDashboard}
              </button>
            ) : !isDriver ? (
              <button
                onClick={onBecomeDriver}
                className="landing-primary-button px-6 py-2.5 text-sm"
              >
                {tBecomeDriver}
              </button>
            ) : null}




            {/* Notification Bell Icon - Only shown on driver dashboard */}
            {isOnDriverDashboard && (
              <div className="relative">
                <button
                  onClick={handleNotificationClick}
                  className="relative rounded-full border border-[#d7eae4] bg-white p-2 text-slate-600 shadow-sm transition-colors hover:text-[#0f766e] focus:outline-none focus:ring-2 focus:ring-[#0f766e] focus:ring-offset-2 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:text-[#5eead4]"
                  aria-label="Notifications"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {/* Notification Badge */}
                  {hasNotifications && (
                    <span className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 border-2 border-white transform translate-x-1 -translate-y-1">
                      <span className="text-white text-xs font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
                    </span>
                  )}
                </button>
              </div>
            )}

      
            {/* User Profile Dropdown */}
            <div className="relative z-[60] group">
              <button className="flex items-center space-x-2 rounded-full border border-[#d7eae4] bg-white px-2.5 py-1.5 text-slate-700 shadow-sm transition-colors hover:text-[#0f766e] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:text-[#5eead4]">
                <div className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)]">
                  {getProfileImageUrl(user?.profilePicture) ? (
                    <img
                      src={getProfileImageUrl(user.profilePicture)}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-full absolute inset-0"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.parentElement?.querySelector('.profile-fallback');
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <span
                    className="profile-fallback text-white font-semibold text-sm"
                    style={{ display: getProfileImageUrl(user?.profilePicture) ? 'none' : 'flex' }}
                  >
                    {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <span className="hidden sm:block text-sm font-medium">{user?.username || 'User'}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */} 
              <div className="invisible absolute right-0 z-[70] mt-2 w-64 rounded-[20px] border border-[#d7eae4] bg-white shadow-[0_28px_60px_-32px_rgba(15,23,42,0.24)] opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 dark:border-slate-800 dark:bg-slate-950">
                <div className="py-2">
                  <div className="border-b border-slate-100 px-4 py-2 dark:border-white/10">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{user?.username || 'User'}</p>
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400" title={user?.email || ''}>{user?.email || ''}</p>
                  </div>
                  <button
                    onClick={onProfile}
                    className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-[#f7fffd] hover:text-[#0f766e] dark:text-slate-200 dark:hover:bg-white/5 dark:hover:text-[#5eead4]"
                  >
                    {tProfile}
                  </button>
                  <div className="mt-1 border-t border-slate-100 dark:border-white/10">
                    <a
                      href="/feedback"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onProfile) onProfile();
                        // Navigate using window.location or navigate hook if available
                        window.location.href = '/feedback';
                      }}
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-[#f7fffd] hover:text-[#0f766e] dark:text-slate-200 dark:hover:bg-white/5 dark:hover:text-[#5eead4]"
                    >
                      {tFeedback}
                    </a>
                    <a
                      href="/terms-conditions"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onProfile) onProfile();
                        window.location.href = '/terms-conditions';
                      }}
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-[#f7fffd] hover:text-[#0f766e] dark:text-slate-200 dark:hover:bg-white/5 dark:hover:text-[#5eead4]"
                    >
                      {tTerms}
                    </a>
                    <a
                      href="/privacy-policy"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onProfile) onProfile();
                        window.location.href = '/privacy-policy';
                      }}
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-[#f7fffd] hover:text-[#0f766e] dark:text-slate-200 dark:hover:bg-white/5 dark:hover:text-[#5eead4]"
                    >
                      {tPrivacy}
                    </a>
                  </div>
                  <div className="mt-1 border-t border-slate-100 dark:border-white/10">
                    <button
                      onClick={toggleTheme}
                      className="flex w-full items-center justify-between px-4 py-2 text-sm text-slate-700 hover:bg-[#f7fffd] hover:text-[#0f766e] dark:text-slate-200 dark:hover:bg-white/5 dark:hover:text-[#5eead4]"
                    >
                      <span className="flex items-center gap-2">
                        {darkMode ? (
                          <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                          </svg>
                        )}
                        {darkMode ? 'Light Mode' : 'Dark Mode'}
                      </span>
                      <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        darkMode ? 'bg-[#0f766e]' : 'bg-slate-300'
                      }`}>
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                          darkMode ? 'translate-x-4.5' : 'translate-x-1'
                        }`} style={{ transform: darkMode ? 'translateX(1rem)' : 'translateX(0.2rem)' }} />
                      </span>
                    </button>
                  </div>
                  <div className="border-t border-slate-100 dark:border-white/10">
                    <button
                      onClick={() => setShowLogoutConfirm(true)}
                      className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                    >
                      {tLogout}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile notification bell - Only shown on driver dashboard (mobile) - Right side */}
          <div className="ml-auto flex items-center gap-2 md:hidden">
            {/* Mobile Language Selector */}
            <LanguageSelector
              aria-label={tLanguage}
              title={tLanguage}
              variant="pill"
              showSelectedCode
              className="border-[#dde7e3] bg-[#f4f7f6] px-2.5 py-2 text-slate-700 shadow-none"
            />
            <button
              type="button"
              onClick={() => navigate('/subscription-pricing')}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#f4f7f6] text-slate-800 shadow-[inset_0_0_0_1px_rgba(15,23,42,0.06)] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition"
              aria-label={tAccount}
            >
              <svg className="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
              </svg>
            </button>
            {isOnDriverDashboard && (
              <>
                {/* Notification Bell */}
                <button
                  onClick={handleNotificationClick}
                  className="relative inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#f4f7f6] text-slate-700 shadow-[inset_0_0_0_1px_rgba(15,23,42,0.06)] transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  aria-label="Notifications"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {/* Notification Badge */}
                  {hasNotifications && (
                    <span className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 border-2 border-white transform translate-x-1 -translate-y-1">
                      <span className="text-white text-xs font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
                    </span>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Mobile Navigation - Show appropriate driver button - Hidden on driver dashboard */}
      {!isOnDriverDashboard && (
        <div className="mt-3 md:hidden">
          <div className="rounded-[24px] bg-white/95 p-2 shadow-[0_16px_34px_-26px_rgba(15,23,42,0.18)] ring-1 ring-black/5">
            {isDriver && !isOnDriverDashboard ? (
              <button
                onClick={onDriverDashboard}
                className="w-full rounded-[18px] bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_34px_-22px_rgba(15,118,110,0.64)] transition-all duration-200"
              >
                {tDriverDashboard}
              </button>
            ) : !isDriver ? (
              <button
                onClick={onBecomeDriver}
                className="w-full rounded-[18px] bg-[linear-gradient(135deg,#0f766e_0%,#065f46_100%)] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_34px_-22px_rgba(15,118,110,0.64)] transition-all duration-200"
              >
                {tBecomeDriver}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        show={showLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          onLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </nav>
  );
};

export default Navbar;

